import { MatchedRecordMetadata, RecordViewFragment } from 'generated/graphql';
import { useCallback, useState } from 'react';
import { Feedback } from 'v2/core/entities/feedback';
import feedbacksMapper from 'v2/core/repository/edge-server/records/mapper';
import { MatchedFeedbackMapType } from 'v2/core/repository/edge-server/types';

export type FetchMoreCallback = ({
  variables: { cursor },
}: {
  variables: { cursor: string };
}) => Promise<any>;

const useFetchMore = (
  fetchMore: FetchMoreCallback,
  cursor?: string,
): [boolean, () => Promise<void>] => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const wrappedFetchMore = useCallback(async () => {
    if (!cursor) {
      return;
    }
    setIsLoadingMore(true);
    await fetchMore({ variables: { cursor } });
    setIsLoadingMore(false);
  }, [cursor, fetchMore]);
  return [isLoadingMore, wrappedFetchMore];
};

interface MatchedFeedback {
  record: Pick<RecordViewFragment, 'id'>;
  matches?: MatchedRecordMetadata[];
}

const mapFeedback = (
  timezone: string,
  feedbacks?: RecordViewFragment[],
  matchedFeedbacks?: MatchedFeedback[],
): Feedback[] | undefined => {
  if (!feedbacks) {
    return undefined;
  }
  return feedbacksMapper(
    feedbacks,
    timezone,
    mapMatchedFeedbacks(matchedFeedbacks),
  );
};

const mapMatchedFeedbacks = (
  matchedFeedbacks: MatchedFeedback[] | undefined,
): MatchedFeedbackMapType => {
  return {
    type: 'SEARCH',
    matchData: matchedFeedbacks?.map((m) => ({
      record: { id: m.record.id },
      matches: m.matches?.map((match) => ({
        type: 'SEARCH',
        startIDx: match.startIDx,
        endIDx: match.endIDx,
        msgIDx: match.msgIDx,
      })),
    })),
  };
};

export { mapFeedback, useFetchMore };
